import React, { useState, useEffect, memo } from "react";
import { COLORS } from "../config/themes";
import Icon from "../components/Icon";
import { Button, Modal, InputField } from "../components";
import { supabase } from "../supabaseClient";

const Toggle = ({ value, onChange }) => (
  <div onClick={() => onChange(!value)} style={{ width: 44, height: 24, borderRadius: 12, background: value ? COLORS.primary : COLORS.border, cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
    <div style={{ position: "absolute", top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: 9, background: "#fff", transition: "left 0.2s" }} />
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 28 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 12 }}>{title}</div>
    <div style={{ background: COLORS.card, borderRadius: 14, border: `1px solid ${COLORS.borderLight}`, padding: 20 }}>{children}</div>
  </div>
);

const SettingRow = ({ label, sub, children, last }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: last ? 0 : 14, marginBottom: last ? 0 : 14, borderBottom: last ? "none" : `1px solid ${COLORS.borderLight}` }}>
    <div><div style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{label}</div>{sub && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{sub}</div>}</div>
    <div style={{ marginLeft: 16, flexShrink: 0 }}>{children}</div>
  </div>
);

const CommunicationsPage = memo(({ schoolSettings, setSchoolSettings, schoolId, isDemo, users, students, classrooms, events, currentUser }) => {
  const [tab, setTab] = useState("digest");
  const [sentHistory, setSentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showAnnounceModal, setShowAnnounceModal] = useState(false);
  const [announceForm, setAnnounceForm] = useState({ subject: "", message: "", audience: "parents" });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const update = (key, val, dbKey) => {
    setSchoolSettings(s => ({ ...s, [key]: val }));
    if (schoolId && !isDemo) supabase.from("schools").update({ [dbKey]: val }).eq("id", schoolId);
  };

  const hours = ["06:00","07:00","08:00","09:00","10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];
  const fmtHour = h => { const n = parseInt(h); return n < 12 ? `${n}:00 AM` : n === 12 ? "12:00 PM" : `${n-12}:00 PM`; };

  const contentToggles = [
    { key: "digestIncludeAttendance", dbKey: "digest_include_attendance", label: "Attendance status", sub: "Show whether the child was present, absent, or late" },
    { key: "digestIncludeActivities", dbKey: "digest_include_activities", label: "Daily log activities", sub: "Meals, naps, learning activities, and mood entries" },
    { key: "digestIncludeEvents", dbKey: "digest_include_events", label: "Upcoming events", sub: "School events happening in the next 7 days" },
    { key: "digestIncludeTeacher", dbKey: "digest_include_teacher", label: "Classroom & teacher", sub: "Child's assigned classroom and teacher name" },
    { key: "digestIncludeChildPhoto", dbKey: "digest_include_child_photo", label: "Child photo", sub: "Child's profile photo in the email" },
  ];

  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

  // Load sent history from Resend logs via edge function
  useEffect(() => {
    if (tab !== "history") return;
    setLoadingHistory(true);
    // We use the audit_log table to track sent digests
    supabase.from("audit_log")
      .select("*")
      .eq("school_id", schoolId)
      .eq("action", "digest_sent")
      .order("created_at", { ascending: false })
      .limit(50)
      .then(({ data }) => { setSentHistory(data || []); setLoadingHistory(false); });
  }, [tab, schoolId]);

  const sendAnnouncement = async () => {
    if (!announceForm.subject || !announceForm.message) return;
    setSending(true);
    setSendResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      // Get recipients
      const recipients = (users || []).filter(u => {
        if (announceForm.audience === "parents") return u.role === "parent";
        if (announceForm.audience === "teachers") return u.role === "teacher";
        if (announceForm.audience === "staff") return u.role === "teacher" || u.role === "admin";
        return u.role !== "parent"; // all staff
      }).filter(u => u.email);

      let sent = 0;
      for (const recipient of recipients) {
        await fetch(`${supabase.supabaseUrl}/functions/v1/send-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${session.access_token}` },
          body: JSON.stringify({
            type: "announcement",
            payload: {
              recipientName: recipient.name,
              recipientEmail: recipient.email,
              subject: announceForm.subject,
              message: announceForm.message,
              schoolName: schoolSettings.schoolName,
              schoolId,
            }
          })
        });
        sent++;
      }
      setSendResult({ success: true, count: sent });
      setShowAnnounceModal(false);
      setAnnounceForm({ subject: "", message: "", audience: "parents" });
    } catch (e) {
      setSendResult({ success: false, error: e.message });
    }
    setSending(false);
  };

  const tabs = [
    { id: "digest", label: "Daily Digest", icon: "mail" },
    { id: "announce", label: "Announcements", icon: "bell" },
    { id: "history", label: "Sent History", icon: "reports" },
  ];

  return (
    <div className="anim-page" style={{ padding: 24, maxWidth: 760 }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 28, fontFamily: "'Nunito', sans-serif", fontWeight: 800, color: COLORS.navy }}>
          <Icon name="mail" size={24} style={{ display: "inline", verticalAlign: "middle", marginRight: 8 }} /> Communications
        </h2>
        <p style={{ margin: 0, color: COLORS.textMuted, fontSize: 14 }}>Manage email communications with parents and staff</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `2px solid ${COLORS.borderLight}` }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "10px 20px", border: "none", borderBottom: `3px solid ${tab === t.id ? COLORS.primary : "transparent"}`, background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 600, color: tab === t.id ? COLORS.primary : COLORS.textMuted, fontFamily: "inherit", transition: "all 0.15s", marginBottom: -2 }}>
            <Icon name={t.icon} size={14} /> {t.label}
          </button>
        ))}
      </div>

      {/* ── DIGEST TAB ── */}
      {tab === "digest" && (<>

        <Section title="Enable & Schedule">
          <SettingRow label="Enable daily digest" sub="Send parents a daily summary of their child's attendance and activities">
            <Toggle value={!!schoolSettings.digestEnabled} onChange={val => update("digestEnabled", val, "digest_enabled")} />
          </SettingRow>
          {schoolSettings.digestEnabled && (<>
            <SettingRow label="Send time" sub={`School timezone: ${schoolSettings.timezone || "America/New_York"}`}>
              <select value={schoolSettings.digestTime || "17:00"} onChange={e => update("digestTime", e.target.value, "digest_time")} style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, outline: "none", cursor: "pointer" }}>
                {hours.map(h => <option key={h} value={h}>{fmtHour(h)}</option>)}
              </select>
            </SettingRow>
            <SettingRow label="Send on days" last>
              <div style={{ display: "flex", gap: 5 }}>
                {days.map(day => {
                  const active = (schoolSettings.digestDays || ["Mon","Tue","Wed","Thu","Fri"]).includes(day);
                  return <div key={day} onClick={() => { const cur = schoolSettings.digestDays || ["Mon","Tue","Wed","Thu","Fri"]; const next = active ? cur.filter(d => d !== day) : [...cur, day]; update("digestDays", next, "digest_days"); }} style={{ padding: "4px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", background: active ? COLORS.primary : COLORS.borderLight, color: active ? "#fff" : COLORS.textMuted, transition: "all 0.15s" }}>{day}</div>;
                })}
              </div>
            </SettingRow>
          </>)}
        </Section>

        {schoolSettings.digestEnabled && (<>

        <Section title="Branding">
          <SettingRow label="Header color" sub="Background color for the email header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <input type="color" value={schoolSettings.digestBrandColor || "#16a34a"} onChange={e => update("digestBrandColor", e.target.value, "digest_brand_color")} style={{ width: 40, height: 32, borderRadius: 6, border: `1px solid ${COLORS.border}`, cursor: "pointer", padding: 2, background: "none" }} />
              <span style={{ fontSize: 12, color: COLORS.textMuted, fontFamily: "monospace" }}>{schoolSettings.digestBrandColor || "#16a34a"}</span>
            </div>
          </SettingRow>
          <SettingRow label="School logo" sub="Shown at the top of every digest email" last>
            <span style={{ fontSize: 13, color: schoolSettings.logo ? COLORS.accent : COLORS.textMuted, fontWeight: 600 }}>
              {schoolSettings.logo ? "✓ Logo set" : "Set in General Settings"}
            </span>
          </SettingRow>
        </Section>

        <Section title="Content sections">
          {contentToggles.map(({ key, dbKey, label, sub }, i) => (
            <SettingRow key={key} label={label} sub={sub} last={i === contentToggles.length - 1 && !schoolSettings.digestIncludeAnnouncement}>
              <Toggle value={schoolSettings[key] !== false} onChange={val => update(key, val, dbKey)} />
            </SettingRow>
          ))}
          <SettingRow label="Custom announcement" sub="Add a pinned message to every digest (e.g. reminders, news)" last={!schoolSettings.digestIncludeAnnouncement}>
            <Toggle value={!!schoolSettings.digestIncludeAnnouncement} onChange={val => update("digestIncludeAnnouncement", val, "digest_include_announcement")} />
          </SettingRow>
          {schoolSettings.digestIncludeAnnouncement && (
            <div style={{ marginTop: 12 }}>
              <textarea value={schoolSettings.digestAnnouncement || ""} onChange={e => setSchoolSettings(s => ({ ...s, digestAnnouncement: e.target.value }))} onBlur={e => { if (schoolId && !isDemo) supabase.from("schools").update({ digest_announcement: e.target.value }).eq("id", schoolId); }} placeholder="e.g. Reminder: Picture day is this Friday! Please dress your child appropriately." style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, outline: "none", resize: "vertical", minHeight: 80, boxSizing: "border-box" }} />
            </div>
          )}
        </Section>

        <Section title="Footer message">
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Custom sign-off shown at the bottom of every digest</div>
          <textarea value={schoolSettings.digestFooterMessage || ""} onChange={e => setSchoolSettings(s => ({ ...s, digestFooterMessage: e.target.value }))} onBlur={e => { if (schoolId && !isDemo) supabase.from("schools").update({ digest_footer_message: e.target.value }).eq("id", schoolId); }} placeholder="e.g. Questions? Call us at (305) 555-1234 — The Nest Team" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, outline: "none", resize: "vertical", minHeight: 70, boxSizing: "border-box" }} />
        </Section>

        </>)}
      </>)}

      {/* ── ANNOUNCEMENTS TAB ── */}
      {tab === "announce" && (<>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy }}>Send a one-off announcement</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Send an email to parents, teachers, or all staff immediately</div>
          </div>
          <Button onClick={() => setShowAnnounceModal(true)}><Icon name="mail" size={14} /> Compose</Button>
        </div>

        {sendResult && (
          <div style={{ padding: 14, borderRadius: 10, background: sendResult.success ? COLORS.accentPale : COLORS.dangerPale, border: `1px solid ${sendResult.success ? COLORS.accent : COLORS.danger}33`, marginBottom: 20, fontSize: 13, color: sendResult.success ? COLORS.accent : COLORS.danger, fontWeight: 600 }}>
            {sendResult.success ? `✓ Announcement sent to ${sendResult.count} recipient${sendResult.count !== 1 ? "s" : ""}` : `✗ Failed: ${sendResult.error}`}
          </div>
        )}

        <Section title="Email types">
          {[
            { icon: "mail", title: "Daily digest", sub: "Automated daily summary for parents — configure in the Daily Digest tab", action: () => setTab("digest") },
            { icon: "students", title: "Enrollment confirmation", sub: "Sent automatically when a student is enrolled from the pipeline", action: null },
            { icon: "bell", title: "Invite emails", sub: "Sent automatically when you invite a teacher or parent via Users", action: null },
          ].map((item, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, paddingBottom: i < 2 ? 14 : 0, marginBottom: i < 2 ? 14 : 0, borderBottom: i < 2 ? `1px solid ${COLORS.borderLight}` : "none" }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, background: COLORS.primaryPale, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name={item.icon} size={18} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.navy }}>{item.title}</div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{item.sub}</div>
              </div>
              {item.action && <button onClick={item.action} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: COLORS.primary, fontWeight: 600, fontFamily: "inherit" }}>Configure →</button>}
              {!item.action && <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 600, background: COLORS.borderLight, padding: "3px 8px", borderRadius: 6 }}>Automatic</span>}
            </div>
          ))}
        </Section>
      </>)}

      {/* ── HISTORY TAB ── */}
      {tab === "history" && (<>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.navy, marginBottom: 16 }}>Recent digest sends</div>
        {loadingHistory ? (
          <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted }}>Loading...</div>
        ) : sentHistory.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: COLORS.textMuted, fontSize: 13, background: COLORS.card, borderRadius: 14, border: `1px solid ${COLORS.borderLight}` }}>
            No digest history yet. Digests are logged here after they are sent.
          </div>
        ) : (
          <div style={{ background: COLORS.card, borderRadius: 14, border: `1px solid ${COLORS.borderLight}`, overflow: "hidden" }}>
            {sentHistory.map((entry, i) => (
              <div key={entry.id} style={{ padding: "14px 20px", borderBottom: i < sentHistory.length - 1 ? `1px solid ${COLORS.borderLight}` : "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.navy }}>{entry.details?.subject || "Daily Digest"}</div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>{entry.details?.recipients || 0} recipients · {new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}</div>
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.accent, background: COLORS.accentPale, padding: "3px 8px", borderRadius: 6 }}>Sent</span>
              </div>
            ))}
          </div>
        )}
      </>)}

      {/* ── Compose Modal ── */}
      <Modal open={showAnnounceModal} onClose={() => setShowAnnounceModal(false)} title="Compose Announcement">
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 6 }}>Send to</label>
          <select value={announceForm.audience} onChange={e => setAnnounceForm(f => ({ ...f, audience: e.target.value }))} style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, outline: "none" }}>
            <option value="parents">All Parents</option>
            <option value="teachers">All Teachers</option>
            <option value="staff">All Staff (Teachers + Admins)</option>
            <option value="everyone">Everyone</option>
          </select>
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>
            {(() => {
              const count = (users || []).filter(u => {
                if (announceForm.audience === "parents") return u.role === "parent";
                if (announceForm.audience === "teachers") return u.role === "teacher";
                if (announceForm.audience === "staff") return u.role === "teacher" || u.role === "admin";
                return true;
              }).filter(u => u.email).length;
              return `${count} recipient${count !== 1 ? "s" : ""} with email addresses`;
            })()}
          </div>
        </div>
        <InputField label="Subject" value={announceForm.subject} onChange={e => setAnnounceForm(f => ({ ...f, subject: e.target.value }))} placeholder="e.g. Important: School closed Friday" />
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, display: "block", marginBottom: 6 }}>Message</label>
          <textarea value={announceForm.message} onChange={e => setAnnounceForm(f => ({ ...f, message: e.target.value }))} placeholder="Write your announcement here..." style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, fontFamily: "inherit", background: COLORS.bg, color: COLORS.text, outline: "none", resize: "vertical", minHeight: 120, boxSizing: "border-box" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <Button variant="secondary" onClick={() => setShowAnnounceModal(false)}>Cancel</Button>
          <Button onClick={sendAnnouncement} disabled={sending || !announceForm.subject || !announceForm.message}>
            {sending ? "Sending..." : <><Icon name="mail" size={14} /> Send Now</>}
          </Button>
        </div>
      </Modal>
    </div>
  );
});

export default CommunicationsPage;
